// Page-memory only: no customer status or identity is persisted in browser storage.
if (!window.hybeePatreonStatus) {
  let pending, checkedAt = 0;
  window.hybeePatreonStatus = function (refresh = false) {
    if (!pending || (refresh && Date.now() - checkedAt > 3000) || Date.now() - checkedAt > 30000) {
      checkedAt = Date.now();
      pending = fetch('https://api.hybee3d.com/api/onboarding/status', {
        credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(20000)
      }).then(async response => {
        if (response.status === 401) return { state: 'guest', signed_in: false, patreon_connected: false, member_pricing: false };
        if (!response.ok) throw new Error('status_unavailable');
        const data = await response.json();
        if (!['guest', 'not_connected', 'paid', 'payment_issue', 'not_confirmed', 'unavailable'].includes(data.state)) throw new Error('invalid_status');
        return data;
      }).catch(() => ({ state: 'unavailable', member_pricing: null }));
    }
    return pending;
  };
}
if (!customElements.get('hybee-patreon-onboarding')) {
  customElements.define('hybee-patreon-onboarding', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.copy = JSON.parse(this.querySelector('[data-onboarding-copy]').textContent);
      this.content = this.querySelector('[data-onboarding-content]');
      this.setupBrowseDisclosure();
      this.storageKey = 'hybee-patreon-prompt:' + (location.pathname === '/cart' ? 'cart' : 'browse');
      try { this.dismissed = sessionStorage.getItem(this.storageKey) === 'dismissed'; } catch {}
      this.addEventListener('click', event => {
        const action = event.target.closest('button')?.dataset.action;
        if (action === 'skip') { this.dismissed = true; try { sessionStorage.setItem(this.storageKey, 'dismissed'); } catch {} this.render(); }
        if (action === 'retry') this.load(true);
      }, { signal: this.controller.signal });
      let focusedAt = Date.now();
      window.addEventListener('focus', () => { if (Date.now() - focusedAt > 30000) { focusedAt = Date.now(); this.load(true); } }, { signal: this.controller.signal });
      this.load();
    }
    setupBrowseDisclosure() {
      if (!this.hasAttribute('data-browse-compact')) return;
      this.browseDisclosure = this.querySelector('[data-browse-disclosure]');
      if (!this.browseDisclosure) {
        this.browseDisclosure = this.node('details', '', 'hpo-browse-disclosure');
        this.browseDisclosure.dataset.browseDisclosure = '';
        const summary = this.node('summary', '', 'hpo-browse-summary');
        const status = this.node('span', this.t('loading'));
        status.setAttribute('data-browse-status', '');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        summary.append(status);
        this.content.before(this.browseDisclosure);
        this.browseDisclosure.append(summary, this.content);
      }
      this.browseSummary = this.browseDisclosure.querySelector('summary');
      this.browseStatus = this.browseDisclosure.querySelector('[data-browse-status]');
      this.browseMedia = window.matchMedia('(max-width: 650px)');
      this.browseMobileOpen ??= false;
      const syncViewport = () => {
        const mobile = this.browseMedia.matches;
        const open = !mobile || this.browseMobileOpen;
        const focusIsInside = this.content.contains(document.activeElement);
        const summaryHadFocus = document.activeElement === this.browseSummary;
        this.browseDisclosure.open = open;
        if (!open && focusIsInside) this.browseSummary.focus({ preventScroll: true });
        if (!mobile && summaryHadFocus) {
          const action = this.content.querySelector('a[href], button:not([disabled])');
          if (action) action.focus({ preventScroll: true });
        }
      };
      this.browseMedia.addEventListener('change', syncViewport, { signal: this.controller.signal });
      this.browseDisclosure.addEventListener('toggle', () => {
        if (this.browseMedia.matches) this.browseMobileOpen = this.browseDisclosure.open;
      }, { signal: this.controller.signal });
      syncViewport();
    }
    updateBrowseSummary() {
      if (!this.browseStatus || !this.status) return;
      const s = this.status;
      const hasDiscount = s.member_pricing === true && Number(s.discount_percent) > 0 && Number(s.discount_percent) <= 100;
      const key = ['guest', 'not_connected'].includes(s.state) ? 'browse_guest'
        : s.state === 'paid' ? (hasDiscount ? 'browse_paid_pricing' : 'browse_paid')
        : s.state === 'payment_issue' ? 'browse_payment'
        : s.state === 'not_confirmed' ? 'browse_pending' : 'browse_unavailable';
      this.browseStatus.textContent = this.t(key);
    }
    disconnectedCallback() { this.controller?.abort(); }
    t(key) { return (this.copy[key] || key).replace('{percent}', String(this.status?.discount_percent ?? '')); }
    node(tag, text, cls) { const node = document.createElement(tag); node.textContent = text; if (cls) node.className = cls; return node; }
    connection() {
      const url = new URL('/connect-patreon', this.dataset.library);
      url.searchParams.set('shop_return', location.pathname + location.search + location.hash);
      return url.href;
    }
    link(key, href, cls = 'hpo-button') { const node = this.node('a', this.t(key), cls); node.href = href; return node; }
    button(key, action) { const node = this.node('button', this.t(key), 'hpo-button hpo-secondary'); node.type = 'button'; node.dataset.action = action; node.disabled = !!this.loading; return node; }
    async load(refresh = false) {
      this.loading = true; if (this.status) this.render();
      const status = await window.hybeePatreonStatus(refresh);
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.status = status; this.loading = false; this.render();
      window.dispatchEvent(new CustomEvent('hybee:patreon-status', { detail: status }));
    }
    render() {
      const s = this.status;
      if (!s) return;
      this.updateBrowseSummary();
      this.content.replaceChildren();
      const disconnected = ['guest', 'not_connected'].includes(s.state);
      const compact = s.state === 'paid' || (disconnected && this.dismissed);
      this.classList.toggle('hpo-compact', compact);
      if (compact) {
        this.content.append(this.node('strong', this.t(s.state === 'paid' ? 'paid' : 'compact')));
        if (s.state === 'paid') this.content.append(this.node('p', this.t(s.member_pricing ? 'pricing' : 'paid_detail')));
        this.content.append(this.link(s.state === 'paid' ? 'connection' : 'connect', this.connection(), 'hpo-link'));
        return;
      }
      const key = disconnected ? 'title' : s.state === 'payment_issue' ? 'payment' : s.state === 'not_confirmed' ? 'pending' : 'unavailable';
      this.content.append(this.node('span', this.t('label'), 'hpo-label'), this.node('h2', this.t(key)));
      this.content.append(this.node('p', this.t(disconnected ? 'instruction' : key + '_detail'), 'hpo-instruction'));
      if (disconnected && s.discount_percent) this.content.append(this.node('p', this.t('discount'), 'hpo-instruction'));
      const actions = this.node('div', '', 'hpo-actions');
      if (disconnected) actions.append(this.link('connect', this.connection()), this.button('skip', 'skip'));
      else {
        if (s.state === 'payment_issue') {
          const payment = this.link('update', 'https://www.patreon.com/cw/HyBee3D/membership');
          payment.target = '_blank'; payment.rel = 'noopener noreferrer'; actions.append(payment);
        }
        actions.append(this.button(this.loading ? 'loading' : 'retry', 'retry'), this.link('review', this.connection(), 'hpo-button hpo-secondary'));
      }
      this.content.append(actions);
      if (disconnected) {
        this.content.append(this.node('p', this.t('free'), 'hpo-footnote'));
        this.content.append(this.link('membership_link', 'https://www.patreon.com/cw/HyBee3D/membership', 'hpo-link'));
      }
    }
  });
}
